import { useState } from "react";
import { Camera, Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Switch } from "../../ui/switch";
import { errText } from "../../auth/EmailCode";
import { Section } from "./ProfileFields";

function Row({ icon: Icon, id, title, hint, checked, disabled, onToggle, children }) {
  return (
    <div className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
      <Icon className="w-5 h-5 text-brand mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="font-semibold text-ink cursor-pointer">{title}</label>
        <p className="text-sm text-body mt-0.5">{hint}</p>
        {children}
      </div>
      <Switch id={id} data-testid={id} checked={checked} disabled={disabled} onCheckedChange={onToggle} />
    </div>
  );
}

export default function ProfileSettings({ p, onChange }) {
  const { checkAuth } = useAuth();
  const [sending, setSending] = useState(false);
  const save = async (key, value, msg) => {
    onChange(key, value);
    try {
      await api.patch("/profile/settings", { [key]: value });
      checkAuth();
      toast.success(msg);
    } catch (e) {
      onChange(key, !value);
      toast.error(errText(e, "We couldn't change that setting. Please try again."));
    }
  };
  const preview = async () => {
    setSending(true);
    try { const { data } = await api.post("/nudge/preview"); toast.success(`Preview sent to ${data.sent_to}.`); } catch (e) { toast.error(errText(e)); } finally { setSending(false); }
  };
  return (
    <Section title="Preferences" testid="profile-settings">
      <div className="divide-y divide-line">
        <Row icon={Camera} id="profile-setting-cv-photo" title="Add my photo to my CVs" checked={Boolean(p.cv_photo) && p.has_photo} disabled={!p.has_photo}
          hint={p.has_photo ? "Starts ticked when you download, and you can still change it each time. Some employers prefer CVs without photos." : "Upload a photo at the top of this page first."}
          onToggle={(v) => save("cv_photo", v, v ? "Your photo will be added to your CVs." : "Your CVs won't include your photo.")} />
        <Row icon={Mail} id="profile-setting-weekly-email" title="Weekly email on Mondays" checked={p.weekly_email !== false}
          hint="Every Monday at 9am: next steps for your work status, your week's progress and a short note from Kesh."
          onToggle={(v) => save("weekly_email", v, v ? "You'll get the weekly email on Mondays." : "Weekly email turned off.")}>
          <button data-testid="profile-weekly-preview" onClick={preview} disabled={sending || !p.email_verified} className="mt-2 gi-btn gi-btn-tertiary gi-btn-sm px-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" aria-hidden="true" />} {p.email_verified ? "Send me a preview" : "Verify your email to get it"}
          </button>
        </Row>
      </div>
    </Section>
  );
}
